import React, { useState, useEffect } from "react";
import axios from "axios";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Gift, Play, Loader2, CheckCircle, AlertCircle } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../hooks/useApi";
import { API } from "../utils/constants";
import { AuthLayout } from "../components/layout/AuthLayout";

const COLORS = {
  principale: '#210875',
  accent: '#897FCB',
  success: '#059669',
};

export const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get("session_id");
  const tokenParam = searchParams.get("activation_token");
  const refCode = searchParams.get("ref");

  const [phase, setPhase] = useState("loading");
  // phases: "loading" | "no_purchase" | "activation_form" | "renewal_confirm" | "error" | "completed"
  const [activationData, setActivationData] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [activationToken, setActivationToken] = useState(tokenParam || null);

  const [formData, setFormData] = useState({ prenom: "", nom: "", password: "" });
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [demoLoading, setDemoLoading] = useState(false);
  const [referralInfo, setReferralInfo] = useState(null);

  const { setAuthToken, login } = useAuth();
  const navigate = useNavigate();
  const api = useApi();

  // Bootstrap : déterminer le phase à afficher
  useEffect(() => {
    // Cas prioritaire : lien de parrainage → redirige vers /subscription qui gère le flow gratuit
    if (refCode && !sessionId && !tokenParam) {
      navigate(`/subscription?ref=${encodeURIComponent(refCode)}`, { replace: true });
      return;
    }
    const init = async () => {
      // Cas 1 : retour de Stripe avec session_id
      if (sessionId) {
        try {
          const res = await axios.post(`${API}/checkout/verify-session`, { session_id: sessionId });
          if (res.data.status !== "paid") {
            setErrorMsg("Ton paiement n'est pas encore confirmé. Patiente quelques instants et recharge la page.");
            setPhase("error");
            return;
          }
          setActivationData(res.data);
          setActivationToken(res.data.activation_token);
          if (res.data.already_consumed) {
            setErrorMsg("Ce compte est déjà activé. Tu peux te connecter directement.");
            setPhase("error");
            return;
          }
          setPhase(res.data.mode === "renewal" ? "renewal_confirm" : "activation_form");
        } catch (e) {
          setErrorMsg(e.response?.data?.detail || "Impossible de vérifier le paiement");
          setPhase("error");
        }
        return;
      }

      // Cas 2 : lien d'activation depuis email
      if (tokenParam) {
        try {
          const res = await axios.get(`${API}/auth/validate-activation-token`, { params: { token: tokenParam } });
          setActivationData(res.data);
          setPhase(res.data.mode === "renewal" ? "renewal_confirm" : "activation_form");
        } catch (e) {
          setErrorMsg(e.response?.data?.detail || "Lien invalide ou expiré");
          setPhase("error");
        }
        return;
      }

      // Cas 3 : pas de token -> rediriger vers subscription
      setPhase("no_purchase");
    };
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Vérifier le code parrainage s'il y en a un en URL
  useEffect(() => {
    if (refCode) {
      api
        .get(`/referral/check/${refCode}`)
        .then((res) => {
          if (res.data.valid) {
            setReferralInfo({ code: refCode, referrerPrenom: res.data.referrer_prenom });
            sessionStorage.setItem("pending_referral_code", refCode);
          }
        })
        .catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refCode]);

  const handleActivate = async (e) => {
    e.preventDefault();
    if (formData.password.length < 6) {
      toast.error("Le mot de passe doit faire au moins 6 caractères");
      return;
    }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/activate-account`, {
        activation_token: activationToken,
        prenom: formData.prenom,
        nom: formData.nom,
        password: formData.password,
      });
      // Auto-login
      setAuthToken(res.data.token, res.data.user);
      sessionStorage.removeItem("pending_referral_code");
      // ⭐ Meta Pixel: Purchase (compte activé = vraie vente confirmée)
      const planPrices = { monthly: 12, annual: 97, earlybird: 8.4 };
      const userPlan = res.data.user?.subscription_plan;
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Purchase', {
          content_name: `Biz360 - ${userPlan}`,
          content_category: 'subscription',
          value: planPrices[userPlan] || activationData?.amount || 0,
          currency: 'EUR',
        });
      }
      toast.success("Compte créé ! Bienvenue sur Biz360 ❤️‍🔥 🎉");
      navigate("/onboarding");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de l'activation");
      setSubmitting(false);
    }
  };

  const handleConfirmRenewal = async () => {
    setSubmitting(true);
    try {
      const res = await axios.post(`${API}/auth/confirm-renewal`, {
        activation_token: activationToken,
      });
      setAuthToken(res.data.token, res.data.user);
      // ⭐ Meta Pixel: Purchase (renouvellement confirmé)
      const planPrices = { monthly: 12, annual: 97, earlybird: 8.4 };
      const userPlan = res.data.user?.subscription_plan;
      if (typeof window.fbq === 'function') {
        window.fbq('track', 'Purchase', {
          content_name: `Biz360 renewal - ${userPlan}`,
          content_category: 'subscription',
          value: planPrices[userPlan] || activationData?.amount || 0,
          currency: 'EUR',
        });
      }
      toast.success("Abonnement renouvelé ! 🎉");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors du renouvellement");
      setSubmitting(false);
    }
  };

  // ======= RENDER =======

  if (phase === "loading") {
    return (
      <AuthLayout>
        <div className="flex flex-col items-center justify-center py-20">
          <Loader2 className="w-10 h-10 animate-spin" style={{ color: COLORS.principale }} />
          <p className="mt-4 text-muted-foreground">Vérification de ton paiement...</p>
        </div>
      </AuthLayout>
    );
  }

  if (phase === "error") {
    return (
      <AuthLayout>
        <div className="animate-slide-up text-center">
          <AlertCircle className="w-16 h-16 mx-auto text-red-500" />
          <h2 className="text-2xl font-bold mt-4" style={{ color: COLORS.principale }}>
            Oups, on a un souci
          </h2>
          <p className="text-muted-foreground mt-2">{errorMsg}</p>
          <div className="mt-6 space-y-2">
            <button
              onClick={() => navigate("/login")}
              data-testid="err-goto-login"
              className="w-full h-11 bg-primary text-primary-foreground rounded-lg font-medium hover:bg-primary/90"
            >
              Me connecter
            </button>
            <button
              onClick={() => navigate("/subscription")}
              className="w-full h-11 border rounded-lg font-medium hover:bg-muted"
            >
              Voir les formules
            </button>
            <a
              href="mailto:t.samblat@gmail.com"
              className="inline-block text-sm text-primary underline mt-2"
            >
              Contacter le support
            </a>
          </div>
        </div>
      </AuthLayout>
    );
  }

  if (phase === "no_purchase") {
    return (
      <AuthLayout>
        <div className="animate-slide-up">
          <h2 className="text-3xl font-bold mb-2" style={{ color: COLORS.principale }}>
            Presque prêt 👋
          </h2>
          <p className="text-muted-foreground mb-6">
            Pour créer ton compte Biz360 <span className="text-red-500">❤️‍🔥</span>, commence par choisir ta formule. Ton compte sera activé juste après le paiement.
          </p>
          <button
            onClick={() => navigate("/subscription")}
            data-testid="goto-subscription"
            className="w-full h-12 rounded-xl text-white font-bold hover:opacity-90 transition-opacity"
            style={{ backgroundColor: COLORS.principale }}
          >
            Choisir ma formule
          </button>

          {/* Bouton démo */}
          <div className="mt-6 pt-6 border-t border-dashed">
            <button
              type="button"
              data-testid="demo-access-btn"
              disabled={demoLoading}
              onClick={async () => {
                setDemoLoading(true);
                try {
                  await login("demo@biz360.fr", "demo2026");
                  toast.success("Bienvenue en mode démo !");
                  navigate("/dashboard");
                } catch (err) {
                  toast.error("Erreur d'accès à la démo");
                }
                setDemoLoading(false);
              }}
              className="w-full py-4 rounded-xl font-semibold transition-all flex items-center justify-center gap-3 border-2 hover:scale-[1.02] hover:shadow-lg"
              style={{
                background: 'linear-gradient(135deg, #E8A87C 0%, #D97706 100%)',
                borderColor: '#D97706',
                color: 'white',
                boxShadow: '0 4px 15px rgba(232, 168, 124, 0.4)'
              }}
            >
              <Play size={20} fill="white" />
              {demoLoading ? "Connexion..." : "Voir la démo en action"}
            </button>
            <p className="text-xs text-center text-muted-foreground mt-2">
              Explore Biz360 ❤️‍🔥 avec des données fictives
            </p>
          </div>

          <p className="mt-6 text-center text-muted-foreground">
            Déjà un compte ?{" "}
            <button onClick={() => navigate("/login")} className="text-primary font-medium hover:underline">
              Se connecter
            </button>
          </p>
        </div>
      </AuthLayout>
    );
  }

  if (phase === "renewal_confirm") {
    return (
      <AuthLayout>
        <div className="animate-slide-up">
          <div className="flex items-center justify-center mb-4">
            <CheckCircle className="w-16 h-16" style={{ color: COLORS.success }} />
          </div>
          <h2 className="text-2xl font-bold mb-2 text-center" style={{ color: COLORS.principale }}>
            Paiement confirmé ✅
          </h2>
          <p className="text-center text-muted-foreground mb-6">
            On a reconnu ton compte. Il te reste juste à confirmer le renouvellement.
          </p>

          <div className="bg-muted/50 rounded-xl p-4 mb-6 space-y-2">
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Email</span>
              <span className="font-medium">{activationData?.email}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Formule</span>
              <span className="font-medium">{activationData?.plan_name}</span>
            </div>
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Montant payé</span>
              <span className="font-medium">{activationData?.amount?.toFixed(2)}€</span>
            </div>
          </div>

          <button
            onClick={handleConfirmRenewal}
            disabled={submitting}
            data-testid="confirm-renewal-btn"
            className="w-full h-12 rounded-xl text-white font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ backgroundColor: COLORS.success }}
          >
            {submitting ? "Confirmation..." : "✅ Confirmer mon renouvellement"}
          </button>
          <p className="text-xs text-center text-muted-foreground mt-3">
            Ton abonnement sera prolongé dès la confirmation.
          </p>
        </div>
      </AuthLayout>
    );
  }

  // phase === "activation_form"
  const planBadges = {
    annual: {
      label: "💎 Annuel · Économise 33% vs mensuel",
      bg: "#059669",
      color: "white",
    },
    earlybird: {
      label: "🎁 Early Bird · -30% exclusif",
      bg: "#8a7fce",
      color: "white",
    },
    monthly: {
      label: "🔄 Mensuel · Sans engagement",
      bg: "#E0E7FF",
      color: "#4338CA",
    },
  };
  const currentBadge = planBadges[activationData?.plan] || null;

  return (
    <AuthLayout>
      <div className="animate-slide-up">
        <div className="mb-4 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-medium" style={{ backgroundColor: '#D1FAE5', color: COLORS.success }}>
          <CheckCircle size={14} /> Paiement reçu · {activationData?.amount?.toFixed(2)}€
        </div>
        {currentBadge && (
          <div
            data-testid="plan-badge"
            className="mb-4 ml-2 inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold"
            style={{ background: currentBadge.bg, color: currentBadge.color }}
          >
            {currentBadge.label}
          </div>
        )}
        <h2 className="text-3xl font-bold mb-2" style={{ color: COLORS.principale }}>
          Dernière étape ✨
        </h2>
        <p className="text-muted-foreground mb-6">
          Crée ton compte et accède à Biz360 <span className="text-red-500">❤️‍🔥</span> immédiatement.
        </p>

        {referralInfo && (
          <div className="mb-6 p-4 bg-gradient-to-r from-purple-50 to-orange-50 border-2 border-purple-200 rounded-xl">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-purple-100 flex items-center justify-center">
                <Gift size={20} className="text-purple-600" />
              </div>
              <div>
                <p className="font-semibold text-purple-700">🎁 Parrainage appliqué !</p>
                <p className="text-sm text-muted-foreground">
                  {referralInfo.referrerPrenom ? `${referralInfo.referrerPrenom} t'offre` : "Tu reçois"}{" "}
                  <strong>1 mois gratuit</strong>
                </p>
              </div>
            </div>
          </div>
        )}

        <form onSubmit={handleActivate} className="space-y-4">
          <div>
            <label className="block text-sm font-medium mb-2">Email</label>
            <input
              type="email"
              value={activationData?.email || ""}
              readOnly
              data-testid="activation-email"
              className="w-full h-11 px-4 rounded-lg border border-input bg-muted/50 text-muted-foreground"
            />
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-2">Prénom</label>
              <input
                type="text"
                data-testid="activation-prenom"
                value={formData.prenom}
                onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                className="w-full h-11 px-4 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Nom</label>
              <input
                type="text"
                data-testid="activation-nom"
                value={formData.nom}
                onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                className="w-full h-11 px-4 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                required
              />
            </div>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">Mot de passe</label>
            <div className="relative">
              <input
                type={showPassword ? "text" : "password"}
                data-testid="activation-password"
                value={formData.password}
                onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                className="w-full h-11 px-4 pr-12 rounded-lg border border-input bg-background focus:outline-none focus:ring-2 focus:ring-ring"
                minLength={6}
                required
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
              </button>
            </div>
            <p className="text-xs text-muted-foreground mt-1">Minimum 6 caractères</p>
          </div>
          <button
            type="submit"
            disabled={submitting}
            data-testid="activation-submit"
            className="w-full h-12 rounded-xl text-white font-bold hover:opacity-90 transition-opacity disabled:opacity-50"
            style={{ backgroundColor: COLORS.principale }}
          >
            {submitting ? "Création..." : "✨ Activer mon compte"}
          </button>
        </form>
      </div>
    </AuthLayout>
  );
};
